// src/components/configuracoes/financeiro/FinanceiroPage.js
import React, { Fragment, useState, useCallback, useEffect } from 'react';
import {
    CreditCard, PlusCircle, Pencil, Trash2, Loader2, XCircle,
    CheckCircle2, AlertTriangle, Clock, Minus, Zap, MoreVertical,
    ToggleLeft, ToggleRight,
} from 'lucide-react';
import PageHeader from '../../layout/PageHeader';
import { usePaymentGateways, useMutateGateways } from '../../../hooks/usePaymentGateways';
import { usePagarmeRecipient } from '../../../hooks/usePagarmeRecipient';
import { useAuth } from '../../../context/auth/AuthProvider';
import useAlert from '../../../hooks/useAlert';
import { FormStepper, FormStepNavigation } from '../../shared/FormStepper';
import ActionsDrawer from '../../common/ActionsDrawer';

const STEP_LABELS = ['Dados do Gateway'];

const GATEWAY_LABELS = {
    stripe:      { label: 'Stripe',       logo: '💳', colorClass: 'bg-indigo-100 text-indigo-800', temPublicKey: true },
    pagarme:     { label: 'Pagar.me',     logo: '💚', colorClass: 'bg-green-100 text-green-800',   temPublicKey: false },
    mercadopago: { label: 'Mercado Pago', logo: '💛', colorClass: 'bg-yellow-100 text-yellow-800', temPublicKey: true },
    paypal:      { label: 'PayPal',       logo: '🔵', colorClass: 'bg-blue-100 text-blue-800',     temPublicKey: false },
    gerencianet: { label: 'Gerencianet',  logo: '🟠', colorClass: 'bg-orange-100 text-orange-800', temPublicKey: true },
};

const GATEWAYS_DISPONIVEIS = Object.keys(GATEWAY_LABELS);

const STATUS_TESTE = {
    sucesso:       { Icon: CheckCircle2, cor: '#22c55e', label: 'OK' },
    aviso:         { Icon: AlertTriangle, cor: '#f59e0b', label: 'Aviso' },
    falha:         { Icon: XCircle,      cor: '#ef4444', label: 'Falha' },
    pendente:      { Icon: Clock,        cor: '#94a3b8', label: 'Pendente' },
    nao_aplicavel: { Icon: Minus,        cor: '#cbd5e1', label: 'N/A' },
};

const FORM_VAZIO = {
    name: '',
    credentials: { secretKey: '', publicKey: '', webhookSecret: '', sandbox: true },
};

const GatewayBadge = ({ name }) => {
    const cfg = GATEWAY_LABELS[name];
    if (!cfg) return <span>{name}</span>;
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 10px', borderRadius: 20, fontSize: '0.8rem', fontWeight: 600 }} className={cfg.colorClass}>
            {cfg.logo} {cfg.label}
        </span>
    );
};

const ResultadoTeste = ({ resultado, onFechar }) => {
    if (!resultado) return null;
    const { testes = [], resumo = {} } = resultado;
    return (
        <div className="content-panel" style={{ borderLeft: '4px solid var(--color-primary)', marginTop: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h4 style={{ fontWeight: 600, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Zap size={18} style={{ color: 'var(--color-primary)' }}/> Resultado do Teste
                </h4>
                <button onClick={onFechar} className="form-close-button" title="Fechar">
                    <XCircle size={18}/>
                </button>
            </div>

            <div style={{ marginBottom: '1rem' }}>
                {testes.map((teste, i) => {
                    const cfg = STATUS_TESTE[teste.status] || STATUS_TESTE.pendente;
                    const { Icon } = cfg;
                    return (
                        <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', padding: '0.5rem 0', borderBottom: i < testes.length - 1 ? '1px solid var(--color-border)' : 'none' }}>
                            <Icon size={18} style={{ color: cfg.cor, flexShrink: 0, marginTop: 1 }}/>
                            <div>
                                <div style={{ fontWeight: 500, fontSize: '0.875rem' }}>{teste.nome}</div>
                                <div style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>{teste.mensagem}</div>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div style={{
                display: 'flex', gap: '1rem', padding: '0.75rem 1rem',
                background: resumo.falha > 0 ? '#fef2f2' : '#f0fdf4',
                borderRadius: 8, fontSize: '0.8rem', fontWeight: 600,
            }}>
                <span style={{ color: '#22c55e' }}>✅ {resumo.sucesso ?? 0} OK</span>
                {(resumo.aviso ?? 0) > 0 && <span style={{ color: '#f59e0b' }}>⚠️ {resumo.aviso} Aviso(s)</span>}
                {(resumo.falha ?? 0) > 0 && <span style={{ color: '#ef4444' }}>❌ {resumo.falha} Falha(s)</span>}
                {(resumo.pendente ?? 0) > 0 && <span style={{ color: '#94a3b8' }}>⏳ {resumo.pendente} Pendente(s)</span>}
                <span style={{ marginLeft: 'auto', color: 'var(--color-text-secondary)' }}>
                    {resumo.sucesso ?? 0} de {resumo.total ?? testes.length} verificações passaram
                </span>
            </div>
        </div>
    );
};

const GatewayForm = ({ gateway, onSalvar, onFechar, isPending }) => {
    const [form, setForm] = useState(() => {
        if (gateway) {
            return {
                name: gateway.name,
                credentials: {
                    secretKey: '',
                    publicKey: gateway.credentials?.publicKey || '',
                    webhookSecret: '',
                    sandbox: gateway.credentials?.sandbox ?? true,
                },
            };
        }
        return FORM_VAZIO;
    });

    const isEditing = !!gateway;
    const gatewayCfg = GATEWAY_LABELS[form.name] || {};

    const handleChange = useCallback((field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
    }, []);

    const handleCredChange = useCallback((field, value) => {
        setForm(prev => ({ ...prev, credentials: { ...prev.credentials, [field]: value } }));
    }, []);

    const handleSubmit = useCallback((e) => {
        e.preventDefault();
        const payload = { name: form.name, credentials: { ...form.credentials } };
        // Omite secretKey e webhookSecret vazios na edição
        if (isEditing) {
            if (!payload.credentials.secretKey) delete payload.credentials.secretKey;
            if (!payload.credentials.webhookSecret) delete payload.credentials.webhookSecret;
        }
        onSalvar(payload);
    }, [form, isEditing, onSalvar]);

    return (
        <div className="content-panel">
            <form onSubmit={handleSubmit}>
                <div className="form-section">
                    <h4 className="form-section-title">
                        {isEditing ? `Editar Gateway: ${GATEWAY_LABELS[gateway.name]?.label}` : 'Configurar Novo Gateway'}
                    </h4>
                </div>

                <FormStepper currentStep={1} steps={STEP_LABELS} />

                {!isEditing && (
                    <div className="form-group">
                        <label className="form-label required">Gateway de Pagamento</label>
                        <select
                            value={form.name}
                            onChange={e => handleChange('name', e.target.value)}
                            className="form-input"
                            required
                        >
                            <option value="">Selecione um gateway...</option>
                            {GATEWAYS_DISPONIVEIS.map(g => (
                                <option key={g} value={g}>{GATEWAY_LABELS[g].logo} {GATEWAY_LABELS[g].label}</option>
                            ))}
                        </select>
                    </div>
                )}

                <div className="form-grid-container-cols-2">
                    <div className="form-group">
                        <label className="form-label required={!isEditing}">
                            Chave Secreta {isEditing && <span style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', fontWeight: 400 }}>(deixe em branco para manter a atual)</span>}
                        </label>
                        <input
                            type="password"
                            value={form.credentials.secretKey}
                            onChange={e => handleCredChange('secretKey', e.target.value)}
                            className="form-input"
                            placeholder={isEditing ? '••••••••••••••••' : 'sk_live_... ou sk_test_...'}
                            required={!isEditing}
                            autoComplete="new-password"
                        />
                    </div>

                    {(form.name === 'stripe' || form.name === 'mercadopago' || form.name === 'gerencianet' || gatewayCfg.temPublicKey) && (
                        <div className="form-group">
                            <label className="form-label">Chave Pública</label>
                            <input
                                type="text"
                                value={form.credentials.publicKey}
                                onChange={e => handleCredChange('publicKey', e.target.value)}
                                className="form-input"
                                placeholder="pk_live_... ou pk_test_..."
                            />
                        </div>
                    )}

                    <div className="form-group">
                        <label className="form-label">
                            Webhook Secret {isEditing && <span style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', fontWeight: 400 }}>(deixe em branco para manter)</span>}
                        </label>
                        <input
                            type="password"
                            value={form.credentials.webhookSecret}
                            onChange={e => handleCredChange('webhookSecret', e.target.value)}
                            className="form-input"
                            placeholder="whsec_... (opcional)"
                            autoComplete="new-password"
                        />
                    </div>
                </div>

                {/* Toggle Sandbox */}
                <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}>
                        <div
                            onClick={() => handleCredChange('sandbox', !form.credentials.sandbox)}
                            style={{
                                width: 44, height: 24, borderRadius: 12, cursor: 'pointer', position: 'relative', flexShrink: 0,
                                background: form.credentials.sandbox ? 'var(--color-primary)' : '#d1d5db',
                                transition: 'background 0.2s',
                            }}
                        >
                            <div style={{
                                position: 'absolute', top: 2, width: 20, height: 20, borderRadius: '50%',
                                background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                                left: form.credentials.sandbox ? 22 : 2,
                                transition: 'left 0.2s',
                            }}/>
                        </div>
                        <span style={{ fontWeight: 500, fontSize: '0.875rem' }}>
                            {form.credentials.sandbox ? 'Modo Sandbox (Testes)' : 'Modo Produção'}
                        </span>
                    </label>

                    {!form.credentials.sandbox && (
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem',
                            padding: '0.6rem 0.875rem', background: '#fef2f2', border: '1px solid #fecaca',
                            borderRadius: 8, color: '#dc2626', fontSize: '0.8125rem', fontWeight: 500,
                        }}>
                            <AlertTriangle size={16} style={{ flexShrink: 0 }}/>
                            Modo Produção — transações financeiras reais serão processadas.
                        </div>
                    )}
                </div>

                <div className="form-actions">
                    <button type="button" onClick={onFechar} className="action-button action-button-secondary">
                        <XCircle size={18} className="icon"/> Cancelar
                    </button>
                    <FormStepNavigation
                        currentStep={1}
                        totalSteps={1}
                        onBack={() => {}}
                        onNext={() => {}}
                        isSubmitting={isPending}
                        submitLabel={isEditing ? 'Salvar Alterações' : 'Configurar Gateway'}
                    />
                </div>
            </form>
        </div>
    );
};

// Recipient ID do Pagar.me (destino do repasse quando o Sinal é cobrado na
// credencial da franquia/matriz — ver server/services/gatewayResolver.js).
// Não se aplica à matriz (ela é o topo da hierarquia, nunca recebe split).
const RecipientSection = () => {
    const { recipientId, isLoading, save, isSaving } = usePagarmeRecipient();
    const [valor, setValor] = useState('');
    const [dirty, setDirty] = useState(false);

    useEffect(() => {
        if (!dirty) setValor(recipientId);
    }, [recipientId, dirty]);

    const handleSalvar = useCallback((e) => {
        e.preventDefault();
        save(valor.trim(), { onSuccess: () => setDirty(false) });
    }, [valor, save]);

    return (
        <div className="content-panel" style={{ marginBottom: '1rem' }}>
            <h4 className="form-section-title">Dados de Recebimento</h4>
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)', marginTop: -8, marginBottom: 12 }}>
                Recipient ID já criado no painel do Pagar.me — usado pra rotear o sinal pra sua conta quando a cobrança
                passa pela credencial da franquia/matriz (só é necessário se você não configurar um gateway próprio abaixo).
            </p>
            <form onSubmit={handleSalvar} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div className="form-group" style={{ flex: 1, minWidth: 240, marginBottom: 0 }}>
                    <label className="form-label">Recipient ID</label>
                    <input
                        type="text"
                        value={valor}
                        onChange={e => { setValor(e.target.value); setDirty(true); }}
                        className="form-input"
                        placeholder="rp_..."
                        disabled={isLoading}
                    />
                </div>
                <button type="submit" className="action-button action-button-primary" disabled={isLoading || isSaving}>
                    {isSaving ? 'Salvando...' : 'Salvar'}
                </button>
            </form>
        </div>
    );
};

const FinanceiroPage = () => {
    const { user } = useAuth();
    const { setAlert } = useAlert();
    const [showForm, setShowForm] = useState(false);
    const [editingGateway, setEditingGateway] = useState(null);
    const [resultadoTeste, setResultadoTeste] = useState(null);
    const [testingId, setTestingId] = useState(null);
    const [selectedGateway, setSelectedGateway] = useState(null);

    const { data: gateways = [], isLoading } = usePaymentGateways();
    const { createGateway, updateGateway, deleteGateway, toggleStatus, testarGateway } = useMutateGateways();

    const gatewayAtivo = gateways.find(g => g.isActive) || null;
    const isPendingMutation = createGateway.isPending || updateGateway.isPending;

    const handleNovoClick = useCallback(() => {
        setEditingGateway(null);
        setShowForm(true);
        setResultadoTeste(null);
    }, []);

    const handleEditClick = useCallback((gateway) => {
        setEditingGateway(gateway);
        setShowForm(true);
        setResultadoTeste(null);
        setSelectedGateway(null);
    }, []);

    const handleFecharForm = useCallback(() => {
        setShowForm(false);
        setEditingGateway(null);
    }, []);

    const handleSalvar = useCallback((payload) => {
        if (editingGateway) {
            updateGateway.mutate({ id: editingGateway._id, ...payload }, {
                onSuccess: handleFecharForm,
            });
        } else {
            createGateway.mutate(payload, {
                onSuccess: handleFecharForm,
            });
        }
    }, [editingGateway, updateGateway, createGateway, handleFecharForm]);

    const handleToggle = useCallback((gateway) => {
        const acao = gateway.isActive ? 'desativar' : 'ativar';
        if (window.confirm(`Deseja ${acao} o gateway "${GATEWAY_LABELS[gateway.name]?.label || gateway.name}"?`)) {
            toggleStatus.mutate(gateway._id, { onSuccess: () => setSelectedGateway(null) });
        }
    }, [toggleStatus]);

    const handleDeletar = useCallback((gateway) => {
        if (gateway.isActive) {
            setAlert('Não é possível excluir um gateway ativo. Desative-o primeiro.', 'danger');
            return;
        }
        if (window.confirm(`Tem certeza que deseja remover o gateway "${GATEWAY_LABELS[gateway.name]?.label || gateway.name}"?`)) {
            deleteGateway.mutate(gateway._id, { onSuccess: () => setSelectedGateway(null) });
        }
    }, [deleteGateway, setAlert]);

    const handleTestar = useCallback(async (gateway) => {
        setTestingId(gateway._id);
        setResultadoTeste(null);
        setSelectedGateway(null);
        testarGateway.mutate(gateway._id, {
            onSuccess: (res) => {
                setResultadoTeste(res.data?.data || null);
                setTestingId(null);
            },
            onError: () => setTestingId(null),
        });
    }, [testarGateway]);

    return (
        <Fragment>
            <div className="page-management-container">
                <PageHeader
                    icon={<CreditCard />}
                    title="Integrações Financeiras"
                    subtitle={!isLoading && gateways.length > 0 ? `${gateways.length} gateway${gateways.length !== 1 ? 's' : ''} configurado${gateways.length !== 1 ? 's' : ''}` : 'Configure os gateways de pagamento da sua conta'}
                    actions={<>
                        <button
                            onClick={showForm ? handleFecharForm : handleNovoClick}
                            className="action-button action-button-primary"
                        >
                            {showForm ? <XCircle size={18} className="icon"/> : <PlusCircle size={18} className="icon"/>}
                            {showForm ? 'Fechar' : 'Configurar Gateway'}
                        </button>
                    </>}
                />

                {user?.role?.scope !== 'matriz' && !showForm && <RecipientSection />}

                {/* Formulário de criação/edição */}
                {showForm && (
                    <GatewayForm
                        gateway={editingGateway}
                        onSalvar={handleSalvar}
                        onFechar={handleFecharForm}
                        isPending={isPendingMutation}
                    />
                )}

                {/* Card do Gateway Ativo */}
                {!isLoading && gatewayAtivo && (
                    <div style={{
                        background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                        border: '1.5px solid #93c5fd', borderRadius: 12,
                        padding: '1.25rem 1.5rem', marginBottom: '1rem',
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                <div style={{ fontSize: '2.5rem', lineHeight: 1 }}>
                                    {GATEWAY_LABELS[gatewayAtivo.name]?.logo || '💳'}
                                </div>
                                <div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: 4 }}>
                                        <span style={{ fontWeight: 700, fontSize: '1.1rem' }}>
                                            {GATEWAY_LABELS[gatewayAtivo.name]?.label || gatewayAtivo.name}
                                        </span>
                                        <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: '0.75rem', fontWeight: 600, background: '#22c55e', color: '#fff' }}>
                                            Ativo
                                        </span>
                                    </div>
                                    <div style={{ fontSize: '0.8rem', color: '#475569', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        {gatewayAtivo.credentials?.sandbox ? (
                                            <span style={{ padding: '1px 8px', borderRadius: 10, background: '#fef3c7', color: '#92400e', fontWeight: 600 }}>Sandbox</span>
                                        ) : (
                                            <span style={{ padding: '1px 8px', borderRadius: 10, background: '#d1fae5', color: '#065f46', fontWeight: 600 }}>Produção</span>
                                        )}
                                        {gatewayAtivo.credentials?.publicKey && (
                                            <span style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#64748b' }}>
                                                pk: {gatewayAtivo.credentials.publicKey.slice(0, 12)}•••
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <button
                                    onClick={() => handleTestar(gatewayAtivo)}
                                    className="action-button action-button-secondary"
                                    disabled={testingId === gatewayAtivo._id}
                                    style={{ fontSize: '0.8125rem' }}
                                >
                                    {testingId === gatewayAtivo._id
                                        ? <Loader2 size={16} className="icon animate-spin"/>
                                        : <Zap size={16} className="icon"/>}
                                    Testar Conexão
                                </button>
                                <button onClick={() => handleEditClick(gatewayAtivo)} className="action-button action-button-secondary" style={{ fontSize: '0.8125rem' }}>
                                    <Pencil size={16} className="icon"/> Editar
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Resultado do Teste */}
                {resultadoTeste && (
                    <ResultadoTeste resultado={resultadoTeste} onFechar={() => setResultadoTeste(null)} />
                )}
            </div>

            {/* Tabela de Gateways */}
            <div className="table-container">
                <div className="table-wrapper">
                    {isLoading ? (
                        <div className="table-loading-state">
                            <Loader2 size={24} className="animate-spin"/>
                            <span>Carregando gateways...</span>
                        </div>
                    ) : gateways.length > 0 ? (
                        <table className="styled-table">
                            <thead>
                                <tr>
                                    <th style={{ textAlign: 'left' }}>Gateway</th>
                                    <th>Ambiente</th>
                                    <th>Chave Pública</th>
                                    <th>Status</th>
                                    <th style={{ textAlign: 'right' }}>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {gateways.map(gateway => (
                                    <tr key={gateway._id} style={{ opacity: gateway.isActive ? 1 : 0.75 }}>
                                        <td style={{ textAlign: 'left' }}>
                                            <GatewayBadge name={gateway.name}/>
                                        </td>
                                        <td>
                                            {gateway.credentials?.sandbox ? (
                                                <span style={{ padding: '2px 8px', borderRadius: 10, background: '#fef3c7', color: '#92400e', fontSize: '0.75rem', fontWeight: 600 }}>Sandbox</span>
                                            ) : (
                                                <span style={{ padding: '2px 8px', borderRadius: 10, background: '#d1fae5', color: '#065f46', fontSize: '0.75rem', fontWeight: 600 }}>Produção</span>
                                            )}
                                        </td>
                                        <td style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>
                                            {gateway.credentials?.publicKey
                                                ? `${gateway.credentials.publicKey.slice(0, 12)}•••`
                                                : <span className="td-empty">—</span>}
                                        </td>
                                        <td>
                                            <span style={{
                                                padding: '2px 10px', borderRadius: 20, fontSize: '0.75rem', fontWeight: 600,
                                                background: gateway.isActive ? '#dcfce7' : '#f1f5f9',
                                                color: gateway.isActive ? '#166534' : '#64748b',
                                            }}>
                                                {gateway.isActive ? 'Ativo' : 'Inativo'}
                                            </span>
                                        </td>
                                        <td style={{ textAlign: 'right' }}>
                                            <button
                                                onClick={() => setSelectedGateway(gateway)}
                                                className="action-icon-button"
                                                title="Ações"
                                            >
                                                <MoreVertical size={18} className="icon"/>
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <div className="table-empty-state">
                            <CreditCard size={48}/>
                            <h3>Nenhum gateway configurado</h3>
                            <p>Configure um gateway de pagamento para começar a processar cobranças.</p>
                            <button onClick={handleNovoClick} className="action-button action-button-primary" style={{ margin: '0 auto' }}>
                                <PlusCircle size={18} className="icon"/> Configurar Primeiro Gateway
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <ActionsDrawer
                open={!!selectedGateway}
                onClose={() => setSelectedGateway(null)}
                title={selectedGateway ? (GATEWAY_LABELS[selectedGateway.name]?.label || selectedGateway.name) : 'Gateway'}
                actions={selectedGateway ? [
                    {
                        key: 'testar',
                        label: 'Testar Conexão',
                        icon: <Zap size={20} className="icon" />,
                        onClick: () => handleTestar(selectedGateway),
                        loading: testingId === selectedGateway._id,
                    },
                    {
                        key: 'toggle',
                        label: selectedGateway.isActive ? 'Desativar' : 'Ativar',
                        icon: selectedGateway.isActive ? <ToggleLeft size={20} className="icon" /> : <ToggleRight size={20} className="icon" />,
                        onClick: () => handleToggle(selectedGateway),
                        loading: toggleStatus.isPending,
                    },
                    {
                        key: 'editar',
                        label: 'Editar',
                        icon: <Pencil size={20} className="icon" />,
                        onClick: () => handleEditClick(selectedGateway),
                    },
                    ...(!selectedGateway.isActive ? [{
                        key: 'excluir',
                        label: 'Remover',
                        icon: <Trash2 size={20} className="icon" />,
                        onClick: () => handleDeletar(selectedGateway),
                        destructive: true,
                        loading: deleteGateway.isPending,
                    }] : []),
                ] : []}
            />
        </Fragment>
    );
};

export default FinanceiroPage;
